/**
 * useNegotiation: Custom hook for negotiation workspace state management.
 * Handles data fetching, polling, and all negotiation actions.
 *
 * @param {number|string|null} workspaceId - The negotiation workspace id to load.
 * @param {number|string|null} userId      - The current user's id.
 * @param {Object}  [options]                            - Optional callbacks/config.
 * @param {(hash: string) => void} [options.onSignatureStateChange]
 *   Fires when the backend's `signature_state_hash` differs from the previous
 *   poll tick. The hash is an opaque SHA-256 hex string that changes whenever
 *   any signature row or document signing-related field changes. Consumers
 *   should use this as a refetch trigger for any open document panel — never
 *   parse the hash or use it to derive UI state directly.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import NegotiationAPI from './negotiationAgent';

const POLL_INTERVAL = 10000; // 10 seconds
// Show the "reconnecting" cue only after this many consecutive poll failures, so
// a single transient blip doesn't flash a warning.
const STALE_AFTER_FAILURES = 2;

const useNegotiation = (workspaceId, userId, options = {}) => {
  const { onSignatureStateChange } = options;
  const [workspace, setWorkspace] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // True when the background status poll has failed repeatedly, so the workspace
  // may be showing stale data. Cleared on the next successful poll.
  const [stale, setStale] = useState(false);

  const prevStatusRef = useRef(null);
  const prevSignatureHashRef = useRef(null);
  const pollRef = useRef(null);
  const pollFailuresRef = useRef(0);

  // Keep the latest onSignatureStateChange in a ref so pollStatus stays
  // stable across renders (otherwise every parent re-render that produces
  // a new callback identity would re-create the interval).
  const onSignatureStateChangeRef = useRef(onSignatureStateChange);
  useEffect(() => {
    onSignatureStateChangeRef.current = onSignatureStateChange;
  }, [onSignatureStateChange]);

  const fetchWorkspace = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const data = await NegotiationAPI.getWorkspace(workspaceId);
      setWorkspace(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch workspace:', err);
      setError(err.message);
    }
  }, [workspaceId]);

  const fetchTimeline = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const data = await NegotiationAPI.getTimeline(workspaceId);
      setTimeline(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch timeline:', err);
      setError(err.message);
    }
  }, [workspaceId]);

  const pollStatus = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const data = await NegotiationAPI.getStatus(workspaceId);
      setStatus(data);
      // A successful tick means we're back in sync.
      pollFailuresRef.current = 0;
      setStale(false);

      const signatureHash = data.signature_state_hash || '';
      // If revision/comment count or signature state hash changed, refetch
      // full timeline and workspace. The hash flips on webhook-driven
      // signature row updates that don't otherwise touch revision_count or
      // comment_count, so without it the workspace would sit on stale data.
      const statusKey = `${data.offer_status}-${data.revision_count}-${data.comment_count}-${signatureHash}`;
      if (prevStatusRef.current && prevStatusRef.current !== statusKey) {
        await fetchTimeline();
        await fetchWorkspace();
      }
      prevStatusRef.current = statusKey;

      // Independently notify subscribers when the signature hash flips so an
      // open DocumentFlowPanel can refetch the active document, even if no
      // other status field changed.
      if (
        prevSignatureHashRef.current !== null &&
        prevSignatureHashRef.current !== signatureHash &&
        typeof onSignatureStateChangeRef.current === 'function'
      ) {
        try {
          onSignatureStateChangeRef.current(signatureHash);
        } catch (cbErr) {
          console.error('onSignatureStateChange callback failed:', cbErr);
        }
      }
      prevSignatureHashRef.current = signatureHash;
    } catch (err) {
      console.error('Status poll failed:', err);
      pollFailuresRef.current += 1;
      if (pollFailuresRef.current >= STALE_AFTER_FAILURES) setStale(true);
    }
  }, [workspaceId, fetchTimeline, fetchWorkspace]);

  // Initial load
  useEffect(() => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }

    const loadAll = async () => {
      setLoading(true);
      try {
        const [wsData, tlData, stData] = await Promise.all([
          NegotiationAPI.getWorkspace(workspaceId),
          NegotiationAPI.getTimeline(workspaceId),
          NegotiationAPI.getStatus(workspaceId),
        ]);
        setWorkspace(wsData);
        setTimeline(tlData);
        setStatus(stData);
        // Seed both refs from the initial fetch so the first poll tick only
        // fires a diff/callback when something actually changed since load.
        const initialHash = stData?.signature_state_hash || '';
        prevStatusRef.current = `${stData?.offer_status}-${stData?.revision_count}-${stData?.comment_count}-${initialHash}`;
        prevSignatureHashRef.current = initialHash;
        pollFailuresRef.current = 0;
        setStale(false);
        setError(null);
      } catch (err) {
        console.error('Failed to load negotiation:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadAll();
  }, [workspaceId]);

  // Polling
  useEffect(() => {
    if (!workspaceId) return;

    pollRef.current = setInterval(pollStatus, POLL_INTERVAL);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [workspaceId, pollStatus]);

  const addComment = useCallback(async (content) => {
    if (!workspaceId || !userId) return;
    try {
      await NegotiationAPI.addComment(workspaceId, userId, { comment_text: content, comment_type: 'general' });
      await fetchTimeline();
    } catch (err) {
      console.error('Failed to add comment:', err);
      setError(err.message);
      throw err;
    }
  }, [workspaceId, userId, fetchTimeline]);

  const submitCounter = useCallback(async (offerId, counterData) => {
    try {
      const result = await NegotiationAPI.submitCounterOffer(offerId, counterData);
      await fetchTimeline();
      await fetchWorkspace();
      return result;
    } catch (err) {
      console.error('Failed to submit counter-offer:', err);
      setError(err.message);
      throw err;
    }
  }, [fetchTimeline, fetchWorkspace]);

  const acceptOffer = useCallback(async (offerId) => {
    try {
      const result = await NegotiationAPI.acceptOffer(offerId);
      await fetchTimeline();
      await fetchWorkspace();
      return result;
    } catch (err) {
      console.error('Failed to accept offer:', err);
      setError(err.message);
      throw err;
    }
  }, [fetchTimeline, fetchWorkspace]);

  const rejectOffer = useCallback(async (offerId, reason) => {
    try {
      const result = await NegotiationAPI.rejectOffer(offerId, reason);
      await fetchTimeline();
      await fetchWorkspace();
      return result;
    } catch (err) {
      console.error('Failed to reject offer:', err);
      setError(err.message);
      throw err;
    }
  }, [fetchTimeline, fetchWorkspace]);

  const withdrawOffer = useCallback(async (offerId, reason) => {
    try {
      const result = await NegotiationAPI.withdrawOffer(offerId, reason);
      await fetchTimeline();
      await fetchWorkspace();
      return result;
    } catch (err) {
      console.error('Failed to withdraw offer:', err);
      setError(err.message);
      throw err;
    }
  }, [fetchTimeline, fetchWorkspace]);

  return {
    workspace,
    timeline,
    status,
    loading,
    error,
    stale,
    // Re-run the status poll now (e.g. a Retry on the stale banner): a success
    // clears `stale` and refetches anything that changed.
    refresh: pollStatus,
    fetchTimeline,
    addComment,
    submitCounter,
    acceptOffer,
    rejectOffer,
    withdrawOffer,
  };
};

export default useNegotiation;
